import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dsClass } from '../../common/constants/dsClasses.js';
import { t } from '../../common/i18n/index.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { SalesType } from '../../generated/api/models.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import {
  googleEcommerceAddPaymentInfo,
  googleEcommerceAddShippingInfo,
  googleEcommerceBeginCheckout,
} from '../../selfservice/actions/index.js';
import { saveFixedBroadbandOrderDraft } from '../../selfservice/common/fixedBroadbandOrderStorage.js';
import { createNettiliteEcommerceItemsFromConfiguredOffer } from '../../selfservice/common/googleEcommerceEvent.js';
import { ContactsEditProvider } from '../ContactsEditContext/ContactsEditContext.js';
import { DeviceCheckoutBillSummaryHeader } from '../DeviceCheckoutBillSummaryHeader/DeviceCheckoutBillSummaryHeader.js';
import { formatNettiliteOfferCheckoutName } from '../OrderSubscriptionSelection/content/NettiLiteOfferSpecification';
import { FixedBroadbandCheckoutStepManager } from './FixedBroadbandCheckoutStepManager.js';
import {
  getAddedAddOns,
  getCheapestCampaignAssociation,
  getConfiguredOffer,
  getGFastDevicePackageAddon,
  getSelectedOffer,
} from './FixedBroadbandUtils.js';
import { StepBillingInformation } from './Steps/StepBillingInformation.js';
import { StepBroadbandProduct } from './Steps/StepBroadbandProduct.js';
import { StepBroadbandSetupDate } from './Steps/StepBroadbandSetupDate.js';
import { StepLoginOrContactInfo } from './Steps/StepLoginOrContactInfo.js';
import { StepProductSelection } from './Steps/StepProductSelection.js';
import type { FixedBroadbandInstallationDetails } from './FixedBroadbandSetupDate.js';
import type { FixedBBCheckoutLoaderData } from '../../common/loaders.js';
import type { AddedAddon } from '../../common/types/addon.js';
import type { CommonError } from '../../common/types/errors.js';
import type { PurposeOfUseOrContact } from '../../common/types/subscription.js';
import type { CommercialProductAddOnVisibilitiesMap } from '../../common/utils/addOnVisibilityUtils.js';
import type { AddOn, AddOnVisibility, Offer, OnlineModel } from '../../generated/api/models.js';
import type { IpAddressAddOnSelection } from '../../public/site/path/FixedBroadbandOrder/fixedBroadbandAddOnUtil.js';
import type { FixedBroadbandOrderDraft } from '../../selfservice/common/fixedBroadbandOrderStorage.js';
import type { State } from '../../selfservice/common/store.js';
import type { AddressSearchMatch, AddressSearchResult } from '../AddressSearch/AddressSearch.js';
import type { YritysWifiSelection } from '../FixedBroadbandProductSelection/FixedBroadbandYritysWifiAddOn.js';

export interface FixedBroadbandCheckoutProps {
  addressSearchMatch: AddressSearchMatch;
  fixedBroadbandOnlineModel: OnlineModel;
  yritystuplaModel?: OnlineModel;
  validationErrors?: CommonError[];
  isMobileProduct: boolean;
  selectedOfferCode: string;
  commercialProductAddOnVisibilities?: CommercialProductAddOnVisibilitiesMap;
  publicAddOnVisibilities?: AddOnVisibility[];
  fixedBBCheckoutLoaderData: FixedBBCheckoutLoaderData;
  addressSearchResult?: AddressSearchResult;
  savedRouterState?: FixedBroadbandOrderDraft['routerState'];
  initialStep1State?: FixedBroadbandOrderDraft['step1Selections'];
  oidcStatePersistenceEnabled?: boolean;
  initialActiveStep?: number;
}

export interface YritystuplaOfferSelection {
  enabled: boolean;
  selectedOffer: Offer | undefined;
}

export interface InstallationServiceAddOnSelection {
  enabled: boolean;
  selectedAddOn?: AddedAddon;
}

export interface CheckoutStepProps {
  enterNextStep: (step: number) => void;
}

const getYritysWifiForSelectedAddOns = (selection: YritysWifiSelection) => {
  if (!selection.enabled || !selection.selectedAddOn) {
    return [];
  }

  const quantitySpecifier = t.P3SF('MESH device');

  const addOns = Array(selection.quantity).fill({
    ...selection.selectedAddOn,
    addOnAttributes: {
      displayNameOverride: `${selection.selectedAddOn.displayName}, ${selection.quantity}×MESH-laite`,
      localizedDisplayNameOverride: `${selection.selectedAddOn.displayName}, ${quantitySpecifier}`,
    },
  });

  addOns.push({
    ...selection.deliveryFeeAddOn,
  });

  return addOns;
};

export const FixedBroadbandCheckout = (props: FixedBroadbandCheckoutProps) => {
  const dispatch = useDispatch();
  const hasRunOnceRef = useRef(false);
  const [activeStepIndex, setActiveStepIndex] = useState(props.initialActiveStep ?? 0);

  const { campaigns } = useSelector(
    (state: State) => ({
      campaigns: state.campaigns,
    }),
    deepEqual
  );
  const companyInfo = useSessionData().companyInfo;

  const [goToNextStep, setGoToNextStep] = useState(() => () => {});
  const [purposeOfUseOrContact, setPurposeOfUseOrContact] = useState<PurposeOfUseOrContact>();
  const [yritystuplaPurposeOfUse, setYritystuplaPurposeOfUse] = useState<PurposeOfUseOrContact>();
  const [broadbandInstallationDetails, setBroadbandInstallationDetails] = useState<FixedBroadbandInstallationDetails>();
  const [additionalAddOns, setAdditionalAddOns] = useState<AddOn[]>([]);
  const [campaignAutoSelect, setCampaignAutoSelect] = useState(props.initialStep1State?.campaignAutoSelect ?? true);
  const [yritystuplaOffer, setYritystuplaOffer] = useState<YritystuplaOfferSelection>(
    props.initialStep1State?.yritystuplaOffer ?? { enabled: false, selectedOffer: undefined }
  );

  const {
    addressSearchMatch,
    fixedBroadbandOnlineModel,
    isMobileProduct,
    selectedOfferCode,
    yritystuplaModel,
    commercialProductAddOnVisibilities,
    publicAddOnVisibilities,
    fixedBBCheckoutLoaderData,
    addressSearchResult,
    savedRouterState,
    oidcStatePersistenceEnabled,
  } = props;

  const campaignContextsFromVoucher = useMemo(() => campaigns?.campaignContexts || [], [campaigns]);
  const campaignVoucherPromotions = useMemo(() => campaigns?.campaigns || [], [campaigns?.campaigns]);
  const selectedOffer = getSelectedOffer(fixedBroadbandOnlineModel.offers, selectedOfferCode);
  const commercialProduct = selectedOffer.commercialProducts[0];
  const [selectedCampaign, setSelectedCampaign] = useState(
    getCheapestCampaignAssociation(commercialProduct, campaignContextsFromVoucher)
  );
  const [yritysWifiSelection, setYritysWifiSelection] = useState<YritysWifiSelection>(
    props.initialStep1State?.yritysWifiSelection ?? { enabled: false, selectedAddOn: undefined, quantity: 1 }
  );

  const [installationServiceSelection, setInstallationServiceSelection] = useState<InstallationServiceAddOnSelection>(
    props.initialStep1State?.installationServiceSelection ?? { enabled: false }
  );
  const [ipAddressAddOnSelection, setIpAddressAddOnSelection] = useState<IpAddressAddOnSelection>(
    props.initialStep1State?.ipAddressAddOnSelection ?? { enabled: false }
  );
  const initialIsGFastDeviceIncluded = props.initialStep1State?.isGFastDeviceIncluded ?? false;
  const [isGFastDeviceIncluded, setGFastDeviceIncluded] = useState(initialIsGFastDeviceIncluded);
  const [additionalAddOnSelections, setAdditionalAddOnSelections] = useState<AddedAddon[]>(
    props.initialStep1State?.additionalAddOnSelections ?? []
  );

  const selectedAddOns = useMemo(
    () => [
      ...(isGFastDeviceIncluded ? getAddedAddOns([getGFastDevicePackageAddon()]) : []),
      ...(installationServiceSelection.enabled ? [installationServiceSelection.selectedAddOn!] : []),
      ...(ipAddressAddOnSelection.enabled ? [ipAddressAddOnSelection.selectedAddOn!] : []),
      ...additionalAddOnSelections,
      ...getYritysWifiForSelectedAddOns(yritysWifiSelection),
    ],
    [
      isGFastDeviceIncluded,
      installationServiceSelection,
      ipAddressAddOnSelection,
      additionalAddOnSelections,
      yritysWifiSelection,
    ]
  );

  const configuredOffer = getConfiguredOffer(
    selectedOffer,
    commercialProduct,
    fixedBroadbandOnlineModel.campaigns || [],
    campaignVoucherPromotions,
    selectedAddOns,
    yritystuplaOffer,
    selectedCampaign,
    campaigns?.voucher
  );

  // First step doesn't fire enterNextStep so we need this.
  const eCommerceItemProducts = createNettiliteEcommerceItemsFromConfiguredOffer(configuredOffer);
  useEffect(() => {
    if (!hasRunOnceRef.current) {
      dispatch(googleEcommerceBeginCheckout(eCommerceItemProducts));
      hasRunOnceRef.current = true;
    }
  }, [dispatch, eCommerceItemProducts]);

  const enterNextStep = useCallback(
    (step: number) => {
      goToNextStep();
      switch (step) {
        case 1: {
          if (oidcStatePersistenceEnabled && addressSearchResult && savedRouterState) {
            saveFixedBroadbandOrderDraft({
              savedAt: Date.now(),
              routerState: savedRouterState,
              addressSearchResult,
              checkoutStep: 1,
              step1Selections: {
                yritystuplaOffer,
                yritysWifiSelection,
                installationServiceSelection,
                ipAddressAddOnSelection,
                isGFastDeviceIncluded,
                additionalAddOnSelections,
                campaignAutoSelect,
              },
            });
          }
          break;
        }
        case 3: {
          dispatch(googleEcommerceAddShippingInfo(eCommerceItemProducts));
          break;
        }
        case 4: {
          dispatch(googleEcommerceAddPaymentInfo(eCommerceItemProducts));
          break;
        }
      }
    },
    [
      dispatch,
      eCommerceItemProducts,
      goToNextStep,
      oidcStatePersistenceEnabled,
      addressSearchResult,
      savedRouterState,
      yritystuplaOffer,
      yritysWifiSelection,
      installationServiceSelection,
      ipAddressAddOnSelection,
      isGFastDeviceIncluded,
      additionalAddOnSelections,
      campaignAutoSelect,
    ]
  );

  useEffect(() => {
    if (campaignAutoSelect) {
      setSelectedCampaign(getCheapestCampaignAssociation(commercialProduct, campaignContextsFromVoucher));
    }
  }, [campaignAutoSelect, campaignContextsFromVoucher, commercialProduct]);

  useEffect(() => {
    if (oidcStatePersistenceEnabled && activeStepIndex === 0 && addressSearchResult && savedRouterState) {
      saveFixedBroadbandOrderDraft({
        savedAt: Date.now(),
        routerState: savedRouterState,
        addressSearchResult,
        checkoutStep: 0,
        step1Selections: {
          yritystuplaOffer,
          yritysWifiSelection,
          installationServiceSelection,
          ipAddressAddOnSelection,
          isGFastDeviceIncluded,
          additionalAddOnSelections,
          campaignAutoSelect,
        },
      });
    }
  }, [
    oidcStatePersistenceEnabled,
    activeStepIndex,
    addressSearchResult,
    savedRouterState,
    yritystuplaOffer,
    yritysWifiSelection,
    installationServiceSelection,
    ipAddressAddOnSelection,
    isGFastDeviceIncluded,
    additionalAddOnSelections,
    campaignAutoSelect,
  ]);

  const steps = [
    {
      id: 'productSelection',
      content: (
        <StepProductSelection
          selectedOffer={selectedOffer}
          yritystuplaModel={yritystuplaModel}
          yritystuplaOffer={yritystuplaOffer}
          setYritystuplaOffer={setYritystuplaOffer}
          yritysWifiSelection={yritysWifiSelection}
          setYritysWifiSelection={setYritysWifiSelection}
          configuredOffer={configuredOffer}
          campaignContextsFromVoucher={campaignContextsFromVoucher}
          campaigns={campaigns}
          selectedCampaign={selectedCampaign}
          installationServiceSelection={installationServiceSelection}
          setInstallationServiceSelection={setInstallationServiceSelection}
          ipAddressAddOnSelection={ipAddressAddOnSelection}
          setIpAddressAddOnSelection={setIpAddressAddOnSelection}
          setGFastDeviceIncluded={setGFastDeviceIncluded}
          setCampaignAutoSelect={setCampaignAutoSelect}
          setSelectedCampaign={setSelectedCampaign}
          fixedBroadbandOnlineModel={fixedBroadbandOnlineModel}
          selectedAddOns={selectedAddOns}
          enterNextStep={enterNextStep}
          commercialProductAddOnVisibilities={commercialProductAddOnVisibilities}
          onAddOnSelectionChange={setAdditionalAddOnSelections}
          publicAddOnVisibilities={publicAddOnVisibilities}
          fixedBBCheckoutLoaderData={fixedBBCheckoutLoaderData}
        />
      ),
      name: t.UV57('Contract and additional services'),
      isVisible: true,
    },
    {
      id: 'loginOrContactInfo',
      content: <StepLoginOrContactInfo enterNextStep={enterNextStep} />,
      name: t.NF60('Customer details'),
      isVisible: true,
    },
    {
      id: 'broadbandProduct',
      content: (
        <StepBroadbandProduct
          addressSearchMatch={addressSearchMatch}
          configuredOffer={configuredOffer}
          selectedCampaign={selectedCampaign}
          companyInfo={companyInfo}
          selectedPurposeOfUseOrContact={purposeOfUseOrContact}
          yritystuplaOffer={yritystuplaOffer}
          setPurposeOfUseOrContact={setPurposeOfUseOrContact}
          setYritystuplaPurposeOfUse={setYritystuplaPurposeOfUse}
          setAdditionalAddOns={setAdditionalAddOns}
          enterNextStep={enterNextStep}
          contacts={fixedBBCheckoutLoaderData.contacts.contacts || []}
        />
      ),
      name: t.M9H0('Subscription and user details'),
      isVisible: true,
    },
    {
      id: 'setupDate',
      content: (
        <StepBroadbandSetupDate
          enterNextStep={enterNextStep}
          isMobileProduct={isMobileProduct}
          setBroadbandInstallationDetails={setBroadbandInstallationDetails}
          fixedBBCheckoutLoaderData={fixedBBCheckoutLoaderData}
        />
      ),
      name: t.SI9A('Installation date'),
      isVisible: !isMobileProduct || installationServiceSelection.enabled,
    },
    {
      id: 'billingInformation',
      content: (
        <StepBillingInformation
          configuredOffer={configuredOffer}
          fixedBroadbandOnlineModel={fixedBroadbandOnlineModel}
          selectedCampaign={selectedCampaign}
          setCampaignAutoSelect={setCampaignAutoSelect}
          additionalAddOns={additionalAddOns}
          addressSearchMatch={addressSearchMatch}
          purposeOfUseOrContact={purposeOfUseOrContact}
          yritystuplaPurposeOfUse={yritystuplaPurposeOfUse}
          broadbandInstallationDetails={broadbandInstallationDetails}
          companyInfo={companyInfo}
          enterNextStep={enterNextStep}
          fixedBBCheckoutLoaderData={fixedBBCheckoutLoaderData}
        />
      ),
      name: t.RPMR('Billing account details'),
      isVisible: true,
    },
  ].filter(step => step.isVisible);

  return (
    <ContactsEditProvider>
      <div className={dsClass.PADDING_TOP_10}>
        <DeviceCheckoutBillSummaryHeader
          commercialProducts={configuredOffer.selectedCommercialProducts}
          deliveryCharges={0}
          hideZeroDeliveryCharges={true}
          salesType={SalesType.NEW_SALE}
        />
        <div className={dsClass.GRID_ROW}>
          <div className={dsClass.GRID_COL_12}>
            <h3 className={`of-fixed-bb-checkout__heading ${dsClass.MARGIN_BOTTOM_6}`}>
              {formatNettiliteOfferCheckoutName(selectedOffer.offerName)}
            </h3>
          </div>
        </div>
        <div className={dsClass.GRID_ROW}>
          <FixedBroadbandCheckoutStepManager
            defaultActiveStepIndex={props.initialActiveStep}
            enableOpenPreviousStep={true}
            getNextStepFn={goNextFunc => setGoToNextStep(() => goNextFunc)}
            onActiveStepChange={setActiveStepIndex}
            steps={steps}
          />
        </div>
      </div>
    </ContactsEditProvider>
  );
};
